import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "@/lib/toast";
import { X, Lock, Eye, MessageSquare, ShieldCheck, Ban, Loader2, SquarePen } from "lucide-react";

export const BUYER_PASS_EVENT = "os:open-buyer-pass";
export const openBuyerPass = () => window.dispatchEvent(new CustomEvent(BUYER_PASS_EVENT));

// One-time upgrade popup for the gated feed. $5 · 7-day pass unlocks full browsing + messaging +
// buying (NOT selling). Logged-out visitors are routed to sign in first (the pass binds to an
// account). Payment reuses the shared one-time Stripe Checkout (same pattern as credits/swag).
export default function BuyerPassModal() {
  const { user, buyerPassEnabled } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const handler = () => setOpen(true);
    window.addEventListener(BUYER_PASS_EVENT, handler);
    return () => window.removeEventListener(BUYER_PASS_EVENT, handler);
  }, []);

  if (!open || !buyerPassEnabled) return null;

  const start = async () => {
    if (!user) {
      setOpen(false);
      navigate("/join");
      toast.info("Sign in or create a free account to unlock full access.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/buyer-pass/checkout", { origin_url: window.location.origin });
      const url = data.checkout_url || data.url;
      if (url) { window.location.href = url; return; }
      toast.error("Could not start checkout.");
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (err.response?.status === 409 && detail?.code === "already_has_access") {
        setOpen(false);
        toast.info(detail.message || "You already have full marketplace access.");
        return;
      }
      toast.error(formatError(detail) || "Payments are temporarily unavailable.");
    } finally { setBusy(false); }
  };

  const perks = [
    { icon: Eye, text: "See every listing — full price and seller, no blur" },
    { icon: MessageSquare, text: "Message any seller and make offers" },
    { icon: ShieldCheck, text: "Buy with full delivery protection" },
    { icon: SquarePen, text: "Full access to the forum" },
    { icon: Ban, text: "Selling is not included — buyers only", off: true },
  ];

  return (
    <div className="fixed inset-0 z-[80] flex items-end sm:items-center justify-center p-0 sm:p-4" data-testid="buyer-pass-modal">
      <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={() => setOpen(false)} />
      <div className="relative w-full sm:max-w-md bg-[#111213] border border-[#2E2F31] animate-fade-up">
        <div className="flex items-center justify-between px-5 py-4 border-b border-[#2E2F31]">
          <h2 className="font-heading text-xl font-bold uppercase tracking-tight flex items-center gap-2 text-[#E8E8EA]">
            <Lock className="w-5 h-5 text-volt" /> Buyer Pass
          </h2>
          <button onClick={() => setOpen(false)} data-testid="buyer-pass-close" className="text-[#8B8D90] hover:text-[#E8E8EA] transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="px-5 py-5">
          <div className="flex items-end gap-2">
            <span className="font-heading font-bold text-[54px] leading-none text-[#DAD6CE]" data-testid="buyer-pass-price">$5</span>
            <span className="font-mono text-[12px] uppercase tracking-wide text-[#8E9195] pb-2">one-time · 7-day full access</span>
          </div>
          <p className="text-[13px] font-body text-[#8B8D90] mt-3 leading-relaxed">
            Just here to buy? Unlock the entire marketplace for a week. No subscription, no auto-renew.
          </p>

          <ul className="mt-5 space-y-2.5">
            {perks.map((p, i) => (
              <li key={i} className={`flex items-start gap-3 text-[14px] ${p.off ? "text-[#76797D]" : "text-[#DAD6CE]"}`}>
                <p.icon className={`w-4 h-4 mt-0.5 shrink-0 ${p.off ? "text-[#4A4B4D]" : "text-[#57682A]"}`} strokeWidth={1.9} />
                <span>{p.text}</span>
              </li>
            ))}
          </ul>

          <button onClick={start} disabled={busy} data-testid="buyer-pass-start"
            className="mt-6 w-full inline-flex items-center justify-center gap-2 bg-volt hover:brightness-110 disabled:opacity-50 disabled:cursor-not-allowed text-black font-heading font-bold uppercase text-base py-3 transition-all">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Lock className="w-4 h-4" />}
            {user ? "Get Buyer Pass — $5" : "Sign in to unlock"}
          </button>
          <p className="mt-3 text-[11px] font-mono text-[#6B6D70] text-center uppercase tracking-wide">
            Selling gear? <button type="button" onClick={() => { setOpen(false); navigate("/pricing"); }} className="text-olive-bright hover:text-volt underline">See seller plans</button>
          </p>
        </div>
      </div>
    </div>
  );
}
